import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

type ReportTargetType = 'task' | 'profile' | 'message';

type ReportDialogProps = {
  open: boolean;
  onClose: () => void;
  targetType: ReportTargetType;
  targetId: number;
  targetLabel: string;
};

const ReportDialog: React.FC<ReportDialogProps> = ({
  open,
  onClose,
  targetType,
  targetId,
  targetLabel,
}) => {
  const { t } = useTranslation();
  const reasonOptions = [
    t('admin.reports.reasons.spam'),
    t('admin.reports.reasons.inappropriate'),
    t('admin.reports.reasons.fraud'),
    t('admin.reports.reasons.harassment'),
    t('admin.reports.reasons.other'),
  ];
  const [reason, setReason] = useState<string>(reasonOptions[0]);
  const [description, setDescription] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!reasonOptions.includes(reason)) {
      setReason(reasonOptions[0]);
    }
  }, [reason, reasonOptions]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      await api.post('/api/v1/reports', {
        target_type: targetType,
        target_id: targetId,
        reason,
        description: description.trim() || undefined,
      });

      setSuccess(t('admin.reports.report_submit_success'));
      setDescription('');
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('admin.reports.report_submit_error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('admin.reports.report_dialog_title', { target: targetLabel })}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'grid', gap: 2 }}>
          <TextField
            select
            label={t('admin.reports.reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            fullWidth
            size="small"
          >
            {reasonOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={t('admin.reports.description_optional')}
            multiline
            rows={4}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            fullWidth
          />

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.close')}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? t('admin.reports.submitting') : t('admin.reports.submit_report')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportDialog;
